import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import type { OverviewChartRow, OverviewKpiSeries } from "@nexus/contracts";
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui";
import { useOverviewFiltersStore } from "../../stores/overviewFilters.store.js";
import { buildChartData, metricsTitle, resolveMetrics } from "./chartModel.js";
import { buildChartCsv, chartCsvFilename, CHART_CSV_MIME } from "../../lib/exportChartCsv.js";
import { downloadText } from "../../lib/download.js";
import { useTenantCurrency } from "@/lib/use-tenant-currency";
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog";
import { isChartableOnOverview } from "../../lib/overviewChartFields.js";
import { MetricSelectorToolbar } from "./MetricSelectorToolbar.js";
import { BarsView } from "./views/BarsView.js";
import { TableView } from "./views/TableView.js";

interface OverviewChartProps {
  chartRows: OverviewChartRow[];
  /** Drives the tooltip's vs-average badge — same source the drawer Info tab uses. */
  kpiSeries: OverviewKpiSeries[];
  /** Raw Topic_Title of the open detail drawer — its category label gets a chip. */
  selectedTopic?: string | null;
  onTopicSelect?: (topicTitle: string) => void;
}

/**
 * Core-Mode (My Brand) OverviewChart: every topic ordered by the active sort metric,
 * each KPI plotted in raw values against its own axis, scaled to that KPI's min/max
 * across the charted topics.
 */
export function OverviewChart({
  chartRows,
  kpiSeries,
  selectedTopic,
  onTopicSelect,
}: OverviewChartProps) {
  const { t } = useTranslation();
  const { symbol: currencySymbol } = useTenantCurrency();
  const { metricCatalog, metricById } = useKpiSelectorCatalog("overview");
  const chartMetrics = useOverviewFiltersStore((s) => s.chartMetrics);
  const chartSortMetricId = useOverviewFiltersStore((s) => s.chartSortMetricId);
  const chartViewMode = useOverviewFiltersStore((s) => s.chartViewMode);
  const addChartMetric = useOverviewFiltersStore((s) => s.addChartMetric);
  const removeChartMetric = useOverviewFiltersStore((s) => s.removeChartMetric);
  const toggleChartMetricSort = useOverviewFiltersStore((s) => s.toggleChartMetricSort);
  const setChartViewMode = useOverviewFiltersStore((s) => s.setChartViewMode);

  const metrics = useMemo(() => resolveMetrics(chartMetrics, metricById), [chartMetrics, metricById]);
  const data = useMemo(
    () => buildChartData(chartRows, metrics, metricById, chartSortMetricId),
    [chartRows, metrics, metricById, chartSortMetricId],
  );

  // Offer only what this chart can actually compute. The chart evaluates formulas
  // client-side against OverviewChartRow, which lacks `avg_watch_time`; a KPI
  // reading it would plot an empty bar with no explanation. Filtering the OPTIONS
  // only — applying this to `metricById` would make an already-selected metric
  // disappear mid-session.
  const selectableCatalog = useMemo(
    () => metricCatalog.filter((entry) => isChartableOnOverview(entry.formula, entry.id)),
    [metricCatalog],
  );

  const title = metricsTitle(metrics);
  const sortMetric = metrics.find((m) => m.id === chartSortMetricId) ?? metrics[0];
  const sortDirection = sortMetric?.sort === "desc" ? "highToLow" : "lowToHigh";
  const subtitle = sortMetric
    ? t("overview.chart.subtitle", {
        metric: sortMetric.label,
        direction: t(`overview.chart.sort.${sortDirection}`),
      })
    : "";

  // Exports exactly what is plotted — same `data`/`metrics` the views render, so the
  // CSV can't drift from the visible top-N or metric selection.
  const handleExport = useCallback(() => {
    downloadText(buildChartCsv(data, metrics), chartCsvFilename(), CHART_CSV_MIME);
  }, [data, metrics]);

  return (
    <div data-overview-chart data-testid="overview-chart" className="rounded-[14px] border border-border bg-card p-[21px]">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-[15px] font-medium leading-[1.47] tracking-[-0.2px] text-foreground">
            {title}
          </h3>
          {subtitle && (
            <p className="flex items-center gap-1 text-[11px] leading-[1.45] text-muted-foreground">
              {subtitle}
              <Tooltip>
                <TooltipTrigger
                  aria-label={t("overview.chart.axisNoteLabel")}
                  className="rounded-full text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Info className="size-3.5" aria-hidden />
                </TooltipTrigger>
                <TooltipContent side="right" className="leading-relaxed">
                  {t("overview.chart.axisNote")}
                </TooltipContent>
              </Tooltip>
            </p>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleExport}
          disabled={data.length === 0}
          data-testid="overview-export"
          className="h-7 shrink-0 rounded-[6px] px-2 py-1.5 text-[12px] text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:hover:bg-popover"
        >
          {t("overview.chart.export")}
        </Button>
      </div>

      <div className="mt-3">
        <MetricSelectorToolbar
          chartMetrics={chartMetrics}
          catalog={selectableCatalog}
          catalogById={metricById}
          sortMetricId={chartSortMetricId}
          viewMode={chartViewMode}
          onAdd={addChartMetric}
          onRemove={removeChartMetric}
          onToggleSort={toggleChartMetricSort}
          onSetViewMode={setChartViewMode}
        />
      </div>

      <div className="mt-4 h-[428px] w-full overflow-hidden">
        {data.length === 0 ? (
          <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
            {t("overview.chart.noData", "No data for the current filters.")}
          </div>
        ) : chartViewMode === "table" ? (
          <TableView
            data={data}
            metrics={metrics}
            currencySymbol={currencySymbol}
            onTopicSelect={onTopicSelect}
          />
        ) : (
          <BarsView
            data={data}
            metrics={metrics}
            currencySymbol={currencySymbol}
            kpiSeries={kpiSeries}
            selectedTopic={selectedTopic}
            onTopicSelect={onTopicSelect}
          />
        )}
      </div>
    </div>
  );
}
